import { clamp, fbm, hash01, hash3, idSeed, smoothstep } from '@/core/rng';
import { BIOMES, type BiomeKey, type Site } from './types';
import { KIND_MIX, KIND_ORDER, VEG_VARIANTS, type VegKind } from './chunk';
import type { WildlifeSpeciesId } from '@/sim/wildlife/types';
import type { Region } from './region';

/**
 * STRAINS — varieties within a host, and the registry that defines them.
 *
 * A strain is a variety of a plant kind or an animal species, identified by a
 * string that fully determines its traits. There is no table, no catalogue
 * file and no stored trait vector, so there is nothing that can go stale and
 * nothing that can dangle.
 *
 * ## The identity rule, and it is the spine of the whole design
 *
 * `strainOf(seed, key)` is **pure and total**. Every strain in the world —
 * baseline or born from history — is reconstructable from its key alone, and
 * nothing about a strain is ever persisted except that key. A restored save
 * therefore cannot name a strain no function can rebuild, and a malformed key
 * cannot crash the world: it reads as the host's ordinary strain.
 *
 * Pure by law 7 and by law 1: no `three`, no `Math.random`, no wall clock, no
 * store, no camera. Everything here is a function of world position, world
 * seed and a key. Importable by `src/sim/` and by `chunkWorker.ts`.
 *
 * ## Why `VEG_VARIANTS` is imported rather than restated
 *
 * `lookOf` has to know how many near-tier builds a kind has, because the
 * ordinary strain wears **all** of them (invariant I1) and only a remarkable
 * one narrows the range. Restating the count here would be a second table
 * that drifts the first time `render/vegetation.ts` gains a build. The import
 * is used only inside function bodies, never at module-evaluation time, so a
 * later `chunk.ts → strain.ts` edge would resolve as an ordinary ES cycle.
 */

// ---------------------------------------------------------------------------
// hosts
// ---------------------------------------------------------------------------

/**
 * What a strain is a variety OF. Nine hosts, not twenty: a host is a plant
 * kind or an animal species, and nothing else in the world has a population
 * that a variety could be a variety of.
 */
export type StrainHost = VegKind | WildlifeSpeciesId;

/**
 * The five plant kinds are 70.6% of every plant on this island and are the
 * five that decide what a country looks like. The four species are the ones
 * whose carrying capacity reads a coefficient a trait can multiply.
 *
 * Excluded, with reasons: `fish` (never migrates here), `scavenger` (carried
 * by carrion, so a trait on it moves nothing), and the nine remaining plant
 * kinds (each under 3% of plants; `snag` is dead wood and has no variety).
 * `exposure` is inert for `predator` — nothing preys on it — and that
 * asymmetry is kept rather than papered over (law 26).
 *
 * Adding a host later costs one entry here and no format change, **but it
 * changes `foragePotential` where it grows**, so it owes a content bump and a
 * prehistory rebuild.
 */
export const STRAIN_HOSTS: readonly StrainHost[] = [
  'broadleaf', 'conifer', 'shrub', 'tuft', 'spreading',
  'grazer', 'browser', 'small_game', 'predator',
];

const HOST_SET: ReadonlySet<string> = new Set<string>(STRAIN_HOSTS);

/**
 * The plant hosts, in `STRAIN_HOSTS` order. Iteration order is determinism
 * wherever a reader aggregates over hosts, so nothing may walk them any other
 * way.
 */
export const PLANT_HOSTS: readonly VegKind[] =
  ['broadleaf', 'conifer', 'shrub', 'tuft', 'spreading'];

/** Per-host hash salt. FNV of the name, so it does not depend on array order. */
const salt = (host: StrainHost): number => idSeed(host);

/** How many near-tier builds this host's drawn form has. Animals have one. */
const variantsOf = (host: StrainHost): number =>
  (VEG_VARIANTS as Partial<Record<string, number>>)[host] ?? 1;

// ---------------------------------------------------------------------------
// the two grains
// ---------------------------------------------------------------------------

/**
 * The hearth lattice — where strains arise. 1024 m, which is exactly four
 * render chunks (so a hearth cell edge is a chunk edge) and is exactly
 * `SOCIAL_CELL_SIZE`, so animal hearths need no second grid.
 *
 * Declared here rather than imported from `sim/social/socialGrid.ts` on the
 * `REFERENCE_RADIUS`/`FOCUS_REFERENCE` precedent: this file is imported by
 * `chunkWorker.ts` and must not drag the social grid into a worker bundle.
 * `test/strain.ts` asserts the two are equal rather than trusting the comment.
 */
export const HEARTH_CELL = 1024;

/**
 * The store lattice — where a shifted mixture is remembered. 260 m, which is
 * `region.cell` (`ISLAND_WORLD.cellSize`) and `CHRONICLE_CELL`, so
 * `region.siteAt` already snaps to it, and it hard-bounds the store at 13,062
 * rows for this island. Asserted equal to `region.cell` in `test/strain.ts`,
 * not imported, for the same reason as above.
 */
export const STRAIN_CELL = 260;

// ---------------------------------------------------------------------------
// keys
// ---------------------------------------------------------------------------

/**
 * A strain's whole identity, and the ONLY thing ever written to a save.
 *
 *   plain  `${host}#`                      — the ordinary strain of a host
 *   root   `${host}@${hx},${hz}`           — the hearth CELL INDEX it arose in
 *   branch `${parentKey}>${bx},${bz}`      — the 260 m CELL INDEX it was isolated in
 *
 * The birth PLACE is in the key, never the birth TIME, so a daughter's traits
 * are pure in the key and a save restored on a different build reconstructs
 * them exactly. At most one branch per (parent, cell), which is what makes the
 * key unique and is also the honest ecology: a cell holds one isolate.
 *
 * Note the two lattices: a root's indices are on `HEARTH_CELL`, a branch's on
 * `STRAIN_CELL`. `Strain.hx/hz` resolves both to **world metres**, so a reader
 * never has to know which lattice a key came off.
 */
export type StrainKey = string;

/**
 * Depth is capped, so key length is bounded and the snapshot's per-row cost
 * cannot grow with the age of the world. A deeper key is not an error, it is
 * simply not a strain: `strainOf` returns the host's ordinary strain.
 */
export const MAX_DEPTH = 3;

/** The ordinary strain of a host — all-ones, no tint, every variant. */
export const ordinaryKey = (host: StrainHost): StrainKey => `${host}#`;

/** The key of the root strain whose hearth is cell (hx, hz) on `HEARTH_CELL`. */
export const rootKey = (host: StrainHost, hx: number, hz: number): StrainKey =>
  `${host}@${hx},${hz}`;

/**
 * The key of the daughter isolated in 260 m cell (bx, bz). Minting the key is
 * pure and lives here; the *gate* that decides when a branch is allowed to
 * happen is history, and belongs to the store (commit 9).
 */
export const branchKey = (parent: StrainKey, bx: number, bz: number): StrainKey =>
  `${parent}>${bx},${bz}`;

// ---------------------------------------------------------------------------
// traits
// ---------------------------------------------------------------------------

/**
 * The four axes. Each is a MULTIPLIER on a coefficient inside a process the
 * simulation already runs — never a quantity added to an outcome (law 14).
 * 1.0 is ordinary in every axis; the ordinary strain is exactly all-ones.
 *
 * Each axis has a PLANT reader and an ANIMAL reader. That is not a
 * compatibility table: `habitat.build` only ever deals with vegetation and
 * `populations.ts` only ever deals with animals, so each system reads the
 * field that means something to it and nothing branches on kind (law 13).
 */
export interface StrainTraits {
  /**
   * What a stand of it puts into the world's food and wood.
   * PLANT  → × the `biome.density` factor in habitat.build's forage and
   *          woody terms.
   * ANIMAL → × `foodPerHead`, and × the head each animal contributes to the
   *          prey sum in `capacity('predator')`.
   */
  yield: number;

  /**
   * How much of its performance survives when the conditions turn.
   * PLANT  → reshapes the rain response inside the same forage expression,
   *          and `processViability`'s `wet`.
   * ANIMAL → divides `EXCESS_MORTALITY` in the die-down branch of `daily()`.
   */
  tolerance: number;

  /**
   * How readily the ambient destructive process takes it.
   * PLANT  → × the fuel expression shared by `FireSim.fuelAt` and
   *          `habitat.build`'s fireRisk.
   * ANIMAL → × its weight in the predation split in `daily()`.
   */
  exposure: number;

  /**
   * How fast a shifted share relaxes, and how far one founder reaches.
   * PLANT  → × the tilt's half-life in `StrainField`, and × the founder
   *          radius a Grasp-transplant seeds.
   * ANIMAL → × how fast `purity` responds to selection.
   *
   * THIS AXIS IS INVISIBLE. It has no look (`lookOf` ignores it) and it does
   * not enter `fitOf`, so it cannot be read off the strain's range either. It
   * is the one thing you can only learn by ACTING, which is what makes the
   * discovery rule a reward instead of a gate.
   */
  spread: number;
}

export type TraitAxis = keyof StrainTraits;

/** The axes in a fixed order. Iteration order is determinism here too. */
export const TRAIT_AXES: readonly TraitAxis[] =
  ['yield', 'tolerance', 'exposure', 'spread'];

/**
 * Per-axis ADVANTAGE weights — how much one unit of each axis moves an outcome
 * a god would want.
 *
 * **STATUS: GUESSED.** These are effect sizes and the probe that measures them
 * is `tools/strain.ts §5`, which forces each axis to its extreme island-wide
 * for 40 god-free years through the real `HabitatSampler`, `WildlifeSystem`,
 * `FireSim` and `SereStore` and reads the change in forage, herd totals and
 * burned area. That probe cannot run until the sim reads a strain (commit 8),
 * so these ship as a placeholder and are the first thing commit 8 re-derives.
 *
 * **The anti-dominance theorem below does not depend on their values**, only
 * on all four being strictly positive — which is why guessing them is safe
 * and why the guess is stated instead of hidden. They are NOT values: `Weaken`
 * exists precisely because high `yield` is sometimes the thing you want gone,
 * so no axis is asserted good (law 22).
 */
export const ADV_W: Readonly<Record<TraitAxis, number>> = {
  yield: 1.0, tolerance: 0.8, exposure: 0.7, spread: 0.6,
};

/**
 * Per-axis span: how far one unit of advantage moves the trait, so a trait
 * lives in `1 ± m·SPAN`.
 *
 * **STATUS: GUESSED**, same probe, same reason. The bound they buy is real
 * whatever the values: with `m ≤ 1` and `|a| ≤ 1` every trait stays inside
 * [1 − SPAN, 1 + SPAN], so no strain can multiply a coefficient by a number
 * that would break the process it multiplies.
 */
export const SPAN: Readonly<Record<TraitAxis, number>> = {
  yield: 0.35, tolerance: 0.35, exposure: 0.40, spread: 0.45,
};

/** −1 on `exposure`: less fuel is the advantage. +1 on the other three. */
export const DIR: Readonly<Record<TraitAxis, 1 | -1>> = {
  yield: 1, tolerance: 1, exposure: -1, spread: 1,
};

/** How odd the mildest remarkable strain is. Below this it is not an event. */
export const REMARKABLE_FLOOR = 0.30;

// ---------------------------------------------------------------------------
// the look
// ---------------------------------------------------------------------------

/** What the eye sees. DERIVED from traits — never an independent roll. */
export interface StrainLook {
  /** Added to the plant's baseline colour in the 0..255 units `chunk.ts` uses. */
  tint: readonly [number, number, number];
  /** Mix weight. An ORDINARY strain has this === 0 EXACTLY (invariant I1). */
  tintW: number;
  /** Which of the host kind's EXISTING near variants it prefers. Never new ones. */
  wears: readonly number[];
}

export interface Strain {
  key: StrainKey;
  host: StrainHost;
  /** The hearth it arose in, in WORLD METRES. Its name and its home. */
  hx: number; hz: number;
  /** 0 for a root or the ordinary strain; +1 per branching. ≤ MAX_DEPTH. */
  depth: number;
  /** Its parent's key. Absent on roots and on the ordinary strain. */
  parentKey?: StrainKey;
  traits: StrainTraits;
  look: StrainLook;
  /**
   * 0 for ordinary, else in [REMARKABLE_FLOOR, 1]. Not a stored rarity roll:
   * it is the magnitude of the advantage vector, and it is what the eye and
   * the card both key on. `m === 0` ⇒ traits are all-ones to the bit.
   */
  m: number;
}

/**
 * The tint each axis contributes at full deviation, in the 0..255 units
 * `chunk.ts` writes colours in.
 *
 * **STATUS: GUESSED**, and the probe that settles them is `tools/strain.ts §8`
 * — ΔRGB and Δluminance of a patch against the surrounding wood, pushed
 * through `tools/palette.ts`'s real ACES-at-1.55 path at 300 m and 3 km. A
 * tint chosen in a hex picker will not exist on screen and this repository has
 * already paid for learning that once: twelve biomes landed between luminance
 * 126 and 141 and a redwood forest was the same colour as a desert. These are
 * a starting point with the right *directions*, not a measured separation.
 *
 * The directions themselves are not arbitrary, and they are the learnability
 * argument: a player who has met three pale strains has learned that pale
 * means drought-holding, on every seed, everywhere — so the look may never be
 * an independent roll.
 */
const TINT_TOLERANCE: readonly [number, number, number] = [26, 14, 40];
/** `chunk.ts`:1362 already knows this sentence: "glaucous: the blue-grey wax
 *  of a plant that has to keep its water." */
const TINT_YIELD: readonly [number, number, number] = [-22, 10, -14];
/** Dense growth is darker and greener; thin growth is paler and greyer. */
const TINT_EXPOSURE: readonly [number, number, number] = [30, 12, -26];
/** Resinous olive-ochre — the colour of something that burns willingly. */

/**
 * How far a remarkable strain narrows the set of near-tier builds it wears, at
 * `m = 1`. 0.5 means the oddest strain of a 7-variant kind wears 4 of the 7.
 *
 * **STATUS: GUESSED.** It is bounded above by a hard constraint rather than by
 * taste: narrowing may only ever *remove* pairs from the realised
 * `(kind, variant)` set, so the LOD-0 draw partition count is non-increasing
 * under any store state. `tools/strain.ts §7` is the probe that confirms it
 * against this session's baseline (mean 23.10 groups per vegetated chunk).
 */
const WEAR_NARROW = 0.5;

/**
 * The look, derived. Total and pure.
 *
 * Deviates from the specification's one-argument signature and must: invariant
 * I1 requires an ordinary strain to wear **the host's full variant range**,
 * and the number of variants is a fact about the host, not about the traits.
 * A one-argument `lookOf` cannot satisfy its own invariant.
 */
export function lookOf(t: StrainTraits, host: StrainHost): StrainLook {
  // The normalised deviation per axis, in [-1, 1]. Exactly 0 when the trait is
  // exactly 1, which is what makes I1 hold to the bit rather than to a
  // tolerance.
  const nY = (t.yield - 1) / SPAN.yield;
  const nT = (t.tolerance - 1) / SPAN.tolerance;
  const nF = (t.exposure - 1) / SPAN.exposure;
  // `spread` contributes nothing. The invisible axis.

  const tint: [number, number, number] = [
    TINT_YIELD[0] * nY + TINT_TOLERANCE[0] * nT + TINT_EXPOSURE[0] * nF,
    TINT_YIELD[1] * nY + TINT_TOLERANCE[1] * nT + TINT_EXPOSURE[1] * nF,
    TINT_YIELD[2] * nY + TINT_TOLERANCE[2] * nT + TINT_EXPOSURE[2] * nF,
  ];

  // The advantage vector is L-infinity normalised, so exactly one axis attains
  // the magnitude and `m` is recoverable from the traits alone — which is what
  // lets this function stay a function of traits. All-ones gives exactly 0.
  const m = Math.max(
    Math.abs(nY), Math.abs(nT), Math.abs(nF),
    Math.abs((t.spread - 1) / SPAN.spread),
  );

  const v = variantsOf(host);
  let wears: readonly number[];
  if (m === 0) {
    // I1: the ordinary strain wears every build its kind has.
    const all: number[] = [];
    for (let i = 0; i < v; i++) all.push(i);
    wears = all;
  } else {
    // Rank the builds by a hash of the traits themselves — same traits, same
    // wardrobe, in any worker and at any rung — and keep the first k. Only ever
    // a SUBSET of what the kind already draws, so the draw partition cannot grow.
    const k = Math.max(1, Math.round(v * (1 - WEAR_NARROW * m)));
    const h = hash3(Math.round(nY * 4096), Math.round(nT * 4096), Math.round(nF * 4096));
    const order: number[] = [];
    for (let i = 0; i < v; i++) order.push(i);
    order.sort((a, b) => {
      const ha = hash3(a, h, 0x51ed), hb = hash3(b, h, 0x51ed);
      return ha === hb ? a - b : ha - hb;
    });
    wears = order.slice(0, k).sort((a, b) => a - b);
  }

  return { tint, tintW: m, wears };
}

// ---------------------------------------------------------------------------
// the anti-upgrade construction
// ---------------------------------------------------------------------------

/**
 * The advantage vector, projected onto the hyperplane `Σ ADV_W·a = 0` and
 * L-infinity normalised.
 *
 * **Theorem (no dominance).** For two strains of one host with advantage
 * vectors `a`, `b` on that hyperplane, `a − b` also lies on it. If `a − b ≥ 0`
 * componentwise and `Σ w·(a−b) = 0` with every `w > 0`, then `a = b`. So no
 * strain of a host can be at-least-as-good on all four axes and strictly
 * better on one — at any magnitude, for any seed, for ever. Scaling by `m` is
 * a positive scalar and the hyperplane is a linear subspace, so `m·a` is on it
 * too, which is what makes "at any magnitude" true rather than nearly true.
 * Ordinary (`a = 0`) is on the plane, so no strain dominates ordinary either:
 * every remarkable strain is worse at something than the ordinary kind.
 *
 * `test/strain.ts` asserts the theorem numerically over 50,000 sampled keys
 * anyway, because the projection is exactly the kind of thing a later hand
 * will "simplify".
 */
function advantageOf(out: number[], gx: number[]): void {
  let dot = 0, ww = 0;
  for (let i = 0; i < 4; i++) {
    const w = ADV_W[TRAIT_AXES[i]];
    dot += w * gx[i];
    ww += w * w;
  }
  const s = dot / ww;
  let peak = 0;
  for (let i = 0; i < 4; i++) {
    out[i] = gx[i] - s * ADV_W[TRAIT_AXES[i]];
    const a = Math.abs(out[i]);
    if (a > peak) peak = a;
  }
  if (peak < 1e-9) {
    // The degenerate case: `g` parallel to ADV_W, measure zero but reachable
    // by a hash. A silent NaN here would poison every chunk in the world, so
    // it is a named branch. The fallback is a fixed vector already on the
    // plane by construction: w0·(1/w0) + w1·(−1/w1) = 0.
    out[0] = 1; out[1] = -ADV_W.yield / ADV_W.tolerance; out[2] = 0; out[3] = 0;
    peak = Math.max(1, Math.abs(out[1]));
  }
  for (let i = 0; i < 4; i++) out[i] /= peak;
}

const ORDINARY_TRAITS: StrainTraits =
  { yield: 1, tolerance: 1, exposure: 1, spread: 1 };

/**
 * A host that could not be parsed out of a malformed key has to resolve to
 * something, because `strainOf` is total. `broadleaf` is the choice; the
 * `Strain` it returns carries the ordinary key of that host, so it is honestly
 * labelled rather than silently pretending to be the key it was handed.
 */
const FALLBACK_HOST: StrainHost = 'broadleaf';

function ordinaryStrain(host: StrainHost): Strain {
  return {
    key: ordinaryKey(host), host, hx: 0, hz: 0, depth: 0,
    traits: { ...ORDINARY_TRAITS },
    look: lookOf(ORDINARY_TRAITS, host),
    m: 0,
  };
}

interface ParsedKey {
  host: StrainHost;
  /** Cell indices per segment: `[hearth]` then one per branching. */
  cells: Array<{ x: number; z: number }>;
  ordinary: boolean;
}

const isInt = (s: string): boolean => /^-?\d+$/.test(s);

/** Parse a key, or null if it is not one this build can rebuild. */
function parseKey(key: StrainKey): ParsedKey | null {
  if (typeof key !== 'string' || key.length === 0 || key.length > 128) return null;
  const segs = key.split('>');
  if (segs.length - 1 > MAX_DEPTH) return null;
  const base = segs[0];

  if (base.endsWith('#')) {
    const host = base.slice(0, -1);
    if (!HOST_SET.has(host)) return null;
    // A plain key with branches hung off it is not a key anyone mints.
    if (segs.length > 1) return null;
    return { host: host as StrainHost, cells: [], ordinary: true };
  }

  const at = base.indexOf('@');
  if (at <= 0) return null;
  const host = base.slice(0, at);
  if (!HOST_SET.has(host)) return null;
  const cells: Array<{ x: number; z: number }> = [];
  for (let i = 0; i < segs.length; i++) {
    const body = i === 0 ? base.slice(at + 1) : segs[i];
    const comma = body.indexOf(',');
    if (comma <= 0) return null;
    const xs = body.slice(0, comma), zs = body.slice(comma + 1);
    if (!isInt(xs) || !isInt(zs)) return null;
    const x = Number(xs), z = Number(zs);
    if (!Number.isSafeInteger(x) || !Number.isSafeInteger(z)) return null;
    cells.push({ x, z });
  }
  return { host: host as StrainHost, cells, ordinary: false };
}

/**
 * How far one branching displaces a daughter from its parent, before
 * re-projection. **STATUS: GUESSED** — the rate it has to be legible against
 * is `tools/strain.ts §9`'s branch rate, which needs the store's settle gate
 * (commit 9). Small on purpose: a daughter that shares nothing with its parent
 * is not descent, it is a new strain wearing a longer name.
 */
const BRANCH_DISPLACE = 0.45;

/**
 * The strain a key names. Pure and TOTAL: a malformed key, an over-deep key,
 * an unknown host or a key from a build that knew more than this one all
 * return the host's ordinary strain rather than throwing. A restored save must
 * never be able to crash the world.
 */
export function strainOf(seed: number, key: StrainKey): Strain {
  const p = parseKey(key);
  if (!p) return ordinaryStrain(FALLBACK_HOST);
  if (p.ordinary) return ordinaryStrain(p.host);

  const host = p.host;
  const hs = salt(host);

  // Walk the lineage from the root outward, so a daughter's advantage is its
  // parent's displaced by one hash — descent that is legible in the traits,
  // not just in the string.
  const a: number[] = [0, 0, 0, 0];
  const g: number[] = [0, 0, 0, 0];
  let m = 0;
  let parentKey: StrainKey | undefined;
  let acc = '';

  for (let d = 0; d < p.cells.length; d++) {
    const c = p.cells[d];
    acc = d === 0 ? rootKey(host, c.x, c.z) : branchKey(acc, c.x, c.z);
    const ks = idSeed(acc);
    for (let i = 0; i < 4; i++) {
      const u = hash01(ks, i, (seed ^ hs) | 0) * 2 - 1;
      g[i] = d === 0 ? u : a[i] + BRANCH_DISPLACE * u;
    }
    advantageOf(a, g);
    const um = hash01(ks, 0x3f, (seed ^ hs ^ 0x2c19) | 0);
    // Squared, so most remarkable strains are mild and the extremes are rare.
    // A linear draw makes every strain an event, which means none of them is.
    const mRoot = REMARKABLE_FLOOR + (1 - REMARKABLE_FLOOR) * um * um;
    m = d === 0 ? mRoot : clamp(m * (0.85 + 0.3 * um), REMARKABLE_FLOOR, 1);
  }
  // The parent is everything before the last '>'. `acc` is the key rebuilt
  // segment by segment, so it is the canonical form of what was handed in —
  // taking the parent off it rather than off `key` means a key that round-trips
  // and a key that does not both produce a parent that reparses.
  const depth = p.cells.length - 1;
  if (depth > 0) parentKey = acc.slice(0, acc.lastIndexOf('>'));

  const traits: StrainTraits = {
    yield: 1 + m * SPAN.yield * DIR.yield * a[0],
    tolerance: 1 + m * SPAN.tolerance * DIR.tolerance * a[1],
    exposure: 1 + m * SPAN.exposure * DIR.exposure * a[2],
    spread: 1 + m * SPAN.spread * DIR.spread * a[3],
  };

  // Hearth in world metres, off whichever lattice the last segment came from.
  const last = p.cells[p.cells.length - 1];
  const cell = depth === 0 ? HEARTH_CELL : STRAIN_CELL;

  return {
    key, host,
    hx: (last.x + 0.5) * cell,
    hz: (last.z + 0.5) * cell,
    depth,
    ...(parentKey ? { parentKey } : {}),
    traits,
    look: lookOf(traits, host),
    m,
  };
}

// ---------------------------------------------------------------------------
// the grain — every number a probe had to produce
// ---------------------------------------------------------------------------

/**
 * The tunables of the baseline, in one object so `tools/strain.ts` can sweep
 * **the real function** rather than a hand-copied one. A probe that reimplements
 * the thing it measures goes stale silently and then lies; this repository has
 * paid for that twice.
 *
 * `GRAIN` below is the shipped, measured set. Nothing may mutate it.
 */
export interface StrainGrain {
  /** Fraction of hearths that are anything other than the ordinary strain. */
  remarkableP: number;
  /** Metres at which a hearth's claim kernel reaches exactly 0. */
  reach: number;
  /** Metres per octave-0 cell of the noise that roughens a claim's border. */
  provM: number;
  /** How hard that noise pushes a claim, as a fraction of it. In [0, 0.95]. */
  provAmp: number;
  /** How many members a mixture may name before the rest folds into ordinary. */
  mixMax: number;
  /**
   * The ordinary strain's standing claim on every point of the island — one
   * constant, not a per-hearth weight.
   *
   * ### Why ordinary is a background rather than a competitor, and the
   * ### measurement that forced it
   *
   * The first build did what §2.2 says literally: nine hearths, each with a
   * claim, ordinary hearths among them. With `remarkableP` near 0.03, eight of
   * the nine hearths around any point are ordinary, so ordinary's summed claim
   * outweighs the single remarkable one everywhere except within a few hundred
   * metres of its hearth. `tools/strain.ts §1c` printed the result: over the
   * five plant hosts, patch area p10 74.4 ha, p50 101.4 ha, p90 108.2 ha,
   * **max 108.2 ha** — 108.16 ha is 16 cells of 6.76 ha, which is exactly one
   * 1024 m hearth cell. Every strain in the world had a country of identical
   * size and square shape, contiguity 1.000, and **a strain's fit had no
   * bearing at all on how much ground it held.** That is a Voronoi diagram
   * with a wobbly edge; it is precisely the thing §2.2 says the provenance
   * noise exists to prevent, and no amount of noise fixes it because the
   * defect is in the arithmetic, not the border.
   *
   * So ordinary is what it actually is: not a strain with hearths, but the
   * ground everything else has to displace. A hearth that fails the remarkable
   * roll contributes no claim; ordinary contributes `ordBg` everywhere. A
   * strain then holds the level set of `fit · kernel · rough > ordBg`, which
   * makes the size of its country **a function of how well the land suits it**
   * — rarity as physics, which is the property §2.3 is arguing for and the one
   * a rarity table cannot give you.
   */
  ordBg: number;
  /**
   * The exponent every claim — remarkable and ordinary alike — is raised to
   * before the mixture is normalised.
   *
   * ### What it buys, and why it is a separate knob from `reach`
   *
   * A power is a MONOTONE transform applied to every claim, so it cannot
   * reorder the claims and therefore cannot change which strain has the
   * largest one. What it changes is how fast one strain's SHARE gives way to
   * another's — the width of the band where both stand together.
   *
   * **With one caveat, and it was found by the assertion that asserted the
   * tidier version and went red.** The mixture is truncated at `mixMax`, and
   * the tail folded into ordinary is a larger fraction of the total at a low
   * exponent than at a high one, so ordinary can win a point under one and
   * lose it under the other. Measured over 65,310 (land cell × plant host)
   * readings, sharp 1 against sharp 14: **untruncated the dominant is
   * identical at 0 of 65,310; truncated at `mixMax` 3 it differs at 229 of
   * 65,310 = 0.351%, every one an ordinary-versus-remarkable flip.** So §1c's
   * geometry is stable to a third of a percent under this knob, not bit-exact,
   * and §1c's shipped figures were taken at the shipped exponent.
   *
   * It exists because the band was measured and it was wrong. With claims
   * entering the normalisation raw, `tools/strain.ts §1e` printed a median
   * ecotone of 590–730 m across every `provM`/`provAmp` row, against a target
   * of 40–150 m at plant grain: not a boundary, a half-kilometre fade nobody
   * would ever notice from 300 m. Reaching for `reach` to fix it would have
   * shrunk every patch at the same time, which is why the two are separate.
   */
  sharp: number;
  /**
   * The zero of the `fitOf` scale — what an ordinary strain reads, everywhere.
   * Distinct from `ordBg`: this one says which side of the grain a tilt is on
   * (the store's decay reads it); that one says how hard ordinary ground is to
   * displace. They were one number until they had to answer two questions.
   */
  fitBase: number;
  /** `fitOf` coefficients on the three visible axes. */
  kY: number; kT: number; kF: number;
}

/**
 * ## The measured baseline. `npx tsx tools/strain.ts` prints all of it.
 *
 * Every number below was produced by `tools/strain.ts §1`, which walks the real
 * `Region` on the 260 m lattice — **13,062 land cells, 883 km², 927 land hearth
 * cells, 8,343 candidate hearths** (§1a, re-measured, not quoted) — calling the
 * functions in this file rather than a copy of them. Each constant names the
 * section that set it and the reading that decided it.
 *
 * ### The two readings that decide whether any of this means anything
 *
 * **A player meets a strain they have not met before every ~1 km** (§1d: p10
 * 260 m, p50 780 m, mean 1,036 m, p90 2,080 m, max 5,200 m along the land
 * graph from ordinary ground to the nearest remarkable patch). The target was
 * 1–2 km, between `tools/fauna.ts`'s 6.55 km "next thing worth looking at"
 * before landmarks and its 1.2 km after. A 10 km chord at the gate altitude
 * crosses a modal 2–3 distinct strains, and 17 chords in 84 crossed none.
 *
 * In seconds that is very short — 3.7 s at the 282 m/s the Presence makes at
 * 300 m of clearance (`ui/presence.ts`:218). **That is the right answer to a
 * question this probe cannot settle**: it says how dense the content is, not
 * how long a player spends on one, and what a player spends time on is the
 * card and the cast, which are commits 5 and 7.
 *
 * **93.0% of the island is the ordinary strain of any given plant kind**
 * (§1b/§1c). That is the number the whole design rests on: if most of the
 * island were not ordinary, remarkable would mean nothing. The second reading
 * beside it — 70.2% of ground has nothing odd about *any* of its five plant
 * kinds — is what a flight actually feels like, and the two are printed
 * together because they are different questions.
 */
export const GRAIN: Readonly<StrainGrain> = Object.freeze({
  // §1b. Swept 0.010 → 0.060 against realised strains — a hearth key that is
  // actually dominant somewhere on land, since a strain nobody's ground
  // carries is not in the world. 0.030 realises **138 plant + 103 animal =
  // 241**, inside the 100–300 target (0.020 gives 151, 0.040 gives 329), and
  // leaves 93.0% of ground ordinary per host.
  remarkableP: 0.030,
  // §1c. Swept 1,536 → 3,400 m against patch area, contiguity and §1d's travel
  // distance. At 2,600 m: **min 6.8 ha, p10 33.8, p50 223.1, p90 419.1, max
  // 689.5 ha, contiguity 0.948**. Target was p50 25–400 ha, rarest under 25,
  // largest under 4,000, contiguity over 0.45 — all four, and 3,400 m starts
  // eating the ordinary ground (84.6%) that §1b needs.
  reach: 2600,
  // §1e. Swept provM 220 → 640 and provAmp 0.25 → 0.75 at the chosen `sharp`.
  // 320 m / 0.55 puts the ecotone at **p10 40 m, p50 70 m, p90 230 m**, inside
  // the 40–150 m target at plant grain.
  provM: 320,
  provAmp: 0.55,
  // §1e. Swept 1 → 18. At the sweep's baseline (provM 430) raw claims gave a
  // median ecotone of **450 m** — a fade, not a boundary; 14 gives 90 m there
  // and 70 m at the shipped provM of 320. 18 would give 50 m, which is inside
  // the target too, but the p90 tail stops telling you anything: a boundary
  // wants to be *sometimes* broad. See `StrainGrain.sharp` for why this is a
  // separate knob from `reach`, and for the one place it is not free.
  sharp: 14,
  // §1f. **0.01% of land points want a fourth member** at share ≥ 0.02, over
  // five hosts × every land cell — far under the 1% bar. 91.5% of points want
  // only one. Three is generous.
  mixMax: 3,
  // §1c. Swept 0.25 → 0.45 at reach 2,600. 0.35 is where patch p50 lands in the
  // middle of the target band with contiguity still 0.948; 0.45 costs both
  // (p50 101 ha, contiguity 0.870) and 0.25 gives the island away (87.6%
  // ordinary).
  ordBg: 0.35,
  // §1g. `fitBase` moves every strain's fit together and `ordBg` absorbs it
  // almost exactly, so it is fixed at the midpoint of the scale by definition:
  // a remarkable strain must be able to be *worse* than ordinary as easily as
  // better, which is what makes it a trade rather than an upgrade.
  fitBase: 0.50,
  // §1g, and this one was nearly shipped decorative. **The null row matters:**
  // with the coefficients at zero — fit constant everywhere — the patch spread
  // is still 4.3× (p10 67.6, p90 290.7 ha), because provenance noise and the
  // coastline produce that much on their own. The first candidates (0.95 /
  // 1.30 / 0.85) gave 4.7×, i.e. **almost exactly the null**: a strain's fit
  // would have had no bearing on how much ground it held, and the "rarity is
  // physics" claim would have been false while every histogram looked healthy.
  //
  // 2.50 / 3.30 / 2.20 gives fit p05 0.222, p50 0.483, p95 0.706 with 1% of
  // readings pinned at 0 and none at 1, and a patch spread of **12.4×**. The
  // next rung (4.00 / 5.30 / 3.50) buys 24.0× but pins 3% at 0 and 1% at 1,
  // and a clamped fit is a rarity table with extra steps.
  kY: 2.50, kT: 3.30, kF: 2.20,
});

// ---------------------------------------------------------------------------
// fit
// ---------------------------------------------------------------------------

/** `standCharacter`'s own `wet`, so a stand and a strain agree what wet is. */
const wet01 = (site: Site): number =>
  clamp(site.rain + Math.log(1 + site.flow) / 9, 0, 1);
/** `standCharacter`'s own `dry`. */
const dry01 = (site: Site): number => clamp(0.34 - site.rain, 0, 1) * 2;
/**
 * THE SHARED fuel expression — `FireSim.fuelAt` and `habitat.build`'s fireRisk
 * are two hand-mirrored copies of exactly this, and commit 8 replaces both
 * with one exported function. Until then this is a third copy and it is said
 * out loud, because a strain's fire axis and the fire simulation disagreeing
 * about which ground burns is the failure that would be found by nothing.
 */
const fuel01 = (site: Site): number =>
  BIOMES[site.biome].density * (1 - site.rain * 0.8);

/**
 * How well this ground suits this strain, 0..1. Does NOT read `spread`, which
 * is what keeps that axis invisible: the range does not reveal it, the look
 * does not reveal it, and acting is the only way to learn it.
 *
 * Three properties are bought here and each is load-bearing:
 *
 * 1. `fuel01` is the fire model's own expression, not a second one.
 * 2. **The axes are correlated by physics, so the interesting corners are
 *    genuinely scarce.** Wet ground does not burn, so a high-`yield`
 *    high-`exposure` strain has nowhere on this island that suits both halves
 *    of it — it lives only where wet ground abuts ground that burns. A player
 *    can *deduce* that and go there. A rarity table gives you nothing to
 *    deduce.
 * 3. `tolerance` is half-invisible: it enters only through `dry01`, so a range
 *    measured in average conditions barely shows it. You see it in the dry
 *    year.
 *
 * Returns 0 on water. That check is here, before any interpolation a caller
 * does, never after — the recorded 260 m land-mask-versus-bicubic-height trap.
 */
export function fitOfWith(s: Strain, site: Site, g: Readonly<StrainGrain>): number {
  if (!site.land) return 0;
  return clamp(
    g.fitBase
    + g.kY * (s.traits.yield - 1) * wet01(site)
    + g.kT * (s.traits.tolerance - 1) * dry01(site)
    - g.kF * (s.traits.exposure - 1) * fuel01(site),
    0, 1,
  );
}

export const fitOf = (s: Strain, site: Site): number => fitOfWith(s, site, GRAIN);

// ---------------------------------------------------------------------------
// the mixture
// ---------------------------------------------------------------------------

/**
 * A mixture at a point: `n` members, shares summing to 1, sorted descending.
 * Filled in place so nothing allocates in a per-plant loop.
 */
export interface StrainMix { n: number; key: StrainKey[]; share: Float32Array }

/** A mixture buffer with room for `cap` members. */
export const newStrainMix = (cap = GRAIN.mixMax): StrainMix =>
  ({ n: 0, key: new Array<StrainKey>(cap).fill(''), share: new Float32Array(cap) });

/**
 * How many hearth cells either side of the point the mixture has to consider,
 * so that no hearth left OUT of the window could still have had weight.
 *
 * A point sits somewhere in its own 1024 m cell; the nearest hearth centre
 * excluded by a window of radius R is at least `(R + 0.5) · HEARTH_CELL` away.
 * Deriving R from `reach` rather than fixing it at 1 is what keeps the field
 * continuous when the window shifts: a fixed 3×3 window with a reach over
 * 1536 m would drop a hearth that still had weight, and the discontinuity
 * would draw the 1024 m lattice on the hillside — the same class of artifact
 * as `keepFraction`'s "hard-edged quadrilateral of bare ground in the middle
 * distance", which is a property of the streamer showing up as a shape in the
 * land. `test/strain.ts` asserts no axis-aligned periodicity at 1024 m.
 */
const windowRadius = (reach: number): number =>
  Math.max(1, Math.ceil(reach / HEARTH_CELL - 0.5));

/** The kernel: 1 at the hearth, exactly 0 at `reach`, smooth between. */
const kernel = (t: number): number => (t >= 1 ? 0 : 1 - smoothstep(0, 1, t));

/**
 * The strain whose hearth is cell (hx, hz) on the `HEARTH_CELL` lattice.
 * Collapses to the host's ORDINARY strain unless the hearth hash clears
 * `remarkableP` — so most hearths are literally the ordinary kind and cost
 * nothing (invariant I1).
 */
export function hearthStrainWith(
  seed: number, host: StrainHost, hx: number, hz: number, remarkableP: number,
): StrainKey {
  return hash01(hx, hz, (seed ^ salt(host)) | 0) < remarkableP
    ? rootKey(host, hx, hz)
    : ordinaryKey(host);
}

export const hearthStrain = (
  seed: number, host: StrainHost, hx: number, hz: number,
): StrainKey => hearthStrainWith(seed, host, hx, hz, GRAIN.remarkableP);

// Scratch, reused across calls. Pure in the only sense that matters: nothing
// survives a call, so two calls with the same arguments return the same thing
// in any order. Allocating these per call would put nine objects and two arrays
// into the per-chunk loop.
const scratchKey: StrainKey[] = [];
const scratchW: number[] = [];

/**
 * The baseline mixture of one host at a point.
 *
 * Weights the REMARKABLE hearths in range by
 *
 *     w_h = fitOf(strain_h, site)
 *         × kernel(|p − hearth_h| / reach)
 *         × (1 + provAmp · (2·fbm(p / provM, seed ^ salt(host) ^ salt(hearth)) − 1))
 *
 * against the ordinary strain's standing claim of `ordBg`, then normalises,
 * sorts descending, keeps the top `mixMax` and folds the rest into ordinary.
 * `StrainGrain.ordBg` carries the measurement that made ordinary a background
 * rather than a ninth competitor.
 *
 * The noise term is what makes a border an irregular coastline instead of a
 * Voronoi cell. Two things about it are deliberate. It is **interpolated noise
 * on the point's own position** — never `hash01(round(wx/K), …)`, which is a
 * hash of a block index, constant inside the block and discontinuous at its
 * edge, and would trade one rectangle for two. And it is **seeded per hearth**:
 * the specification writes one noise term per point, but a factor common to
 * every hearth cancels in the normalisation and roughens nothing. Per-hearth
 * seeding is the same expression doing what it was written to do.
 */
export function mixAtWith(
  region: Region, host: StrainHost, wx: number, wz: number,
  out: StrainMix, g: Readonly<StrainGrain>,
): void {
  const site = region.siteAt(wx, wz);
  const seed = region.seed;
  const hs = salt(host);
  const R = windowRadius(g.reach);
  const h0x = Math.floor(wx / HEARTH_CELL), h0z = Math.floor(wz / HEARTH_CELL);
  const ord = ordinaryKey(host);

  scratchKey.length = 0;
  scratchW.length = 0;
  // Ordinary's standing claim. Present at every point on the island whether or
  // not any hearth is in range, which is what makes untouched ground ordinary
  // by construction rather than by a fallback branch.
  let total = Math.pow(g.ordBg, g.sharp);

  for (let dz = -R; dz <= R; dz++) for (let dx = -R; dx <= R; dx++) {
    const hx = h0x + dx, hz = h0z + dz;
    const key = hearthStrainWith(seed, host, hx, hz, g.remarkableP);
    if (key === ord) continue;          // a failed hearth claims nothing
    const cx = (hx + 0.5) * HEARTH_CELL, cz = (hz + 0.5) * HEARTH_CELL;
    const k = kernel(Math.hypot(wx - cx, wz - cz) / g.reach);
    if (k <= 0) continue;
    const fit = fitOfWith(strainOf(seed, key), site, g);
    if (fit <= 0) continue;             // includes every point at sea
    const nseed = (seed ^ hs ^ hash3(hx, hz, 0x7a17)) | 0;
    const rough = 1 + g.provAmp * (2 * fbm(wx / g.provM, wz / g.provM, 3, nseed) - 1);
    const w = Math.pow(fit * k * rough, g.sharp);
    if (w <= 0) continue;
    total += w;
    // At most one hearth per cell, and one key per cell, so no key repeats.
    scratchKey.push(key);
    scratchW.push(w);
  }

  if (total <= 0) {
    // `ordBg` is positive, so this is unreachable — but a zero here would
    // divide the world by zero, and a named branch costs one comparison.
    out.n = 1; out.key[0] = ord; out.share[0] = 1;
    return;
  }

  // Sort the remarkable claims descending, ties broken on the key string so the
  // answer cannot depend on window traversal order in a later refactor.
  const order: number[] = [];
  for (let i = 0; i < scratchKey.length; i++) order.push(i);
  order.sort((a, b) => (scratchW[b] - scratchW[a]) || (scratchKey[a] < scratchKey[b] ? -1 : 1));

  const cap = Math.max(1, Math.min(g.mixMax, out.key.length));
  let kept = 0, keptW = 0;
  for (let i = 0; i < order.length && kept < cap - 1; i++) {
    const j = order[i];
    out.key[kept] = scratchKey[j];
    out.share[kept] = scratchW[j] / total;
    keptW += scratchW[j];
    kept++;
  }
  // Ordinary is always the remainder: whatever the kept members did not claim,
  // including the tail that was folded in. That is the honest reading of a
  // truncated mixture — the ground you did not name is ordinary ground.
  out.key[kept] = ord;
  out.share[kept] = Math.max(0, (total - keptW) / total);
  kept++;
  out.n = kept;

  // Re-sort the whole mixture descending so `drawStrain`'s partition and any
  // "what is dominant here" reader see the same order.
  if (out.n > 1) {
    const idx: number[] = [];
    for (let i = 0; i < out.n; i++) idx.push(i);
    idx.sort((a, b) => (out.share[b] - out.share[a]) || (out.key[a] < out.key[b] ? -1 : 1));
    const ks = idx.map((i) => out.key[i]);
    const ss = idx.map((i) => out.share[i]);
    for (let i = 0; i < out.n; i++) { out.key[i] = ks[i]; out.share[i] = ss[i]; }
  }
}

export const mixAt = (
  region: Region, host: StrainHost, wx: number, wz: number, out: StrainMix,
): void => mixAtWith(region, host, wx, wz, out, GRAIN);

/**
 * Which member of the mixture THIS individual is.
 *
 * Partitions the unit interval by cumulative share, indexed by a hash of the
 * individual's own position at quarter-metre resolution — the identical
 * discipline `Instance.variant` uses (`chunk.ts`:1404), which is why it is
 * LOD-stable and worker-stable for free.
 */
export function drawStrain(
  mix: StrainMix, wx: number, wz: number, seed: number,
): number {
  const u = hash01(Math.round(wx * 4), Math.round(wz * 4), (seed + 0x5721) | 0);
  let acc = 0;
  for (let i = 0; i < mix.n; i++) {
    acc += mix.share[i];
    if (u < acc) return i;
  }
  return mix.n > 0 ? mix.n - 1 : 0;
}

/** The mixture's dominant member. Empty string where there is no mixture. */
export const dominantOf = (mix: StrainMix): StrainKey =>
  mix.n > 0 ? mix.key[0] : '';

/** Whether a key names the ordinary strain of its host. Cheap, no parse. */
export const isOrdinary = (key: StrainKey): boolean => key.endsWith('#');

/**
 * The host a key belongs to, or null for a key no build minted. Cheap — the
 * host is the prefix before `#` or `@` — and exact, because both characters
 * are structural: neither can appear in a host name. The field's tilts carry
 * keys of every host through one store, and a reader asking about ONE host's
 * ground (the forage term, a plant's own strain) must not let a grazer tilt
 * answer for broadleaf.
 */
export function hostOfKey(key: StrainKey): StrainHost | null {
  const hash = key.indexOf('#'), at = key.indexOf('@');
  const cut = hash >= 0 && (at < 0 || hash < at) ? hash : at;
  if (cut <= 0) return null;
  const host = key.slice(0, cut);
  return HOST_SET.has(host) ? (host as StrainHost) : null;
}

/**
 * How much of a biome's vegetation each PLANT HOST is — `KIND_MIX`'s own
 * shares over the five hosted kinds, normalised, cached per biome.
 *
 * This is the blend weight the ecology readers use: a biome's forage is grown
 * by all its plants, the five hosts are 70.6% of them island-wide, and the
 * un-hosted kinds have no strains and read as ordinary — so a biome whose
 * plants are mostly un-hosted (marsh's reeds) is honestly near-immune to
 * plant strains, and one that is mostly one host (oak woodland at 0.60
 * `spreading`) takes that host's strains nearly at full weight. The share of
 * un-hosted kinds is returned as `rest`, which reads as multiplier 1.
 */
export interface HostShare { host: VegKind; share: number }
const hostShareCache = new Map<string, { shares: HostShare[]; rest: number }>();
export function hostShares(biome: BiomeKey): { shares: readonly HostShare[]; rest: number } {
  const hit = hostShareCache.get(biome);
  if (hit) return hit;
  const mix = KIND_MIX[biome];
  let total = 0;
  const shares: HostShare[] = [];
  for (const host of PLANT_HOSTS) {
    const w = mix[KIND_ORDER.indexOf(host)] ?? 0;
    total += w;
    if (w > 0) shares.push({ host, share: w });
  }
  // KIND_MIX rows are already normalised over all 21 kinds, so `total` is the
  // hosted share of the biome and the remainder is strain-free ground.
  const out = { shares, rest: Math.max(0, 1 - total) };
  hostShareCache.set(biome, out);
  return out;
}
